<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import FilialCadastro from '@/components/organizacoes/FilialCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const props = defineProps({
  itemParaEditar: { type: Object, default: null }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'changesDetected'])

const metadataStore = useMetadataListasStore()

const mapaCampos = {
  razaoSocial: 'Razão Social',
  nomeFantasia: 'Nome Fantasia'
}

const {
  item,
  isLoading: isSaving,
  isEditing,
  entityInfo,
  errors,
  handleCancel,
  focarPrimeiroErro,
  initializeForm
} = useFormulario(props, emit, {
  mapaCampos,
  idKey: 'idOrganizacao'
})

const mostrarAuditoria = ref(false)

const accordionState = reactive({
  basico: true,
  filiais: true
})

const listas = reactive({})

const isLoadingData = ref(false)

// --- Estados de Painel Lateral (Filial) ---
const isFilialPanelOpen = ref(false)
const filialSelecionada = ref(null)

const toggleFilialPanel = () => {
  isFilialPanelOpen.value = !isFilialPanelOpen.value
}

// --- Inicialização do Item ---
if (!props.itemParaEditar) {
  item.value = {
    razaoSocial: '',
    nomeFantasia: '',
    lstRegimeTributario: null,
    filiais: []
  }
} else {
  // Garantir que filiais seja um array
  if (!item.value.filiais) item.value.filiais = []
}

// --- Carregamento de Listas ---
async function carregarListas () {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['Organizacao', 'Filial', 'LocalEstoque'])
    Object.assign(listas, result)
  } catch (e) {
    console.error('Erro ao carregar listas auxiliares:', e)
  }
}

onMounted(async () => {
  isLoadingData.value = true
  try {
    await carregarListas()
    // Buscar organizações do tenant ativo (o banco já é escopado pelo cliente)
    const res = await api.get('/Organizacoes')
    if (res.data && res.data.length > 0) {
      const org = res.data[0]
      // Preencher o item com os dados da organização encontrada
      item.value = { ...org }
      await buscarFiliais()
    }
  } catch (error) {
    console.error('Erro ao inicializar dados da organização:', error)
  } finally {
    isLoadingData.value = false
  }
})

async function buscarFiliais () {
  if (!item.value.idOrganizacao) return
  try {
    const res = await api.get(`/Filiais?idOrganizacao=${item.value.idOrganizacao}`)
    item.value.filiais = res.data

    // Buscar todos os locais de estoque e agrupar por idFilial
    // para exibir a contagem correta no grid (a listagem de filiais não inclui locaisEstoque)
    try {
      const resLocais = await api.get('/LocaisEstoque', { params: { pageSize: 9999 } })
      const locais = Array.isArray(resLocais.data) ? resLocais.data : []
      const locaisPorFilial = {}
      for (const local of locais) {
        const idFilial = local.idFilial
        if (!locaisPorFilial[idFilial]) locaisPorFilial[idFilial] = []
        locaisPorFilial[idFilial].push(local)
      }
      for (const filial of item.value.filiais) {
        filial.locaisEstoque = locaisPorFilial[filial.idFilial] || []
      }
    } catch (e) {
      console.error('Erro ao buscar locais de estoque para contagem:', e)
      emit('show-notification', { message: 'Erro ao carregar contagem de locais de estoque.', type: 'error' })
    }
  } catch (error) {
    console.error('Erro ao buscar filiais:', error)
    emit('show-notification', { message: 'Erro ao carregar lista de filiais.', type: 'error' })
  }
}

// --- Lógica de Filiais (Agora via Painel Lateral) ---
function iniciarNovaFilial () {
  filialSelecionada.value = {
    _isNew: true,
    idFilial: 0,
    cnpj: '',
    inscricaoEstadual: '',
    uf: '',
    codigoMunicipioIbge: '',
    enderecoDescricao: '',
    logradouro: '',
    cep: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    pais: 'Brasil',
    locaisEstoque: []
  }
  isFilialPanelOpen.value = true
}

function editarFilial (fil, idx) {
  filialSelecionada.value = JSON.parse(JSON.stringify({ ...fil, _index: idx, _isNew: false }))
  isFilialPanelOpen.value = true
}

async function handleFilialAtualizada () {
  await buscarFiliais()
  isFilialPanelOpen.value = false
  filialSelecionada.value = null
}

// --- Submissão Final (customizada - NÃO usa submitForm para evitar emit itemAtualizado) ---
async function salvarOrganizacao () {
  if (isSaving.value) return

  // --- Validação Frontend (mesma lógica do useFormulario) ---
  // Limpeza correta: preserva o Proxy reativo
  for (const key in errors.value) { delete errors.value[key] }
  let isValid = true
  const labelsObrigatorios = document.querySelectorAll('label.is-required')

  labelsObrigatorios.forEach(label => {
    if (label.offsetParent === null) return
    const inputId = label.getAttribute('for')
    if (!inputId) return
    const inputElement = document.getElementById(inputId)
    if (!inputElement) return

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || inputElement.value.trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      errors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      isValid = false
    }
  })

  if (!isValid) {
    const primeiraMensagemErro = Object.values(errors.value)[0] || 'Por favor, preencha todos os campos obrigatórios destacados em vermelho.'
    emit('show-notification', { message: primeiraMensagemErro, type: 'warning' })
    await focarPrimeiroErro()
    return
  }

  // --- Chamada Direta à API (sem submitForm para não emitir itemAtualizado) ---
  isSaving.value = true

  try {
    let response
    const payload = JSON.parse(JSON.stringify(item.value))

    if (isEditing.value) {
      response = await api.put(`/Organizacoes/${item.value.idOrganizacao}`, payload)
    } else {
      response = await api.post('/Organizacoes', payload)
    }

    // Atualiza o formulário com a resposta e reseta o snapshot interno
    initializeForm(response.data)

    // Buscar filiais novamente para garantir IDs actualizados
    await buscarFiliais()

    const acao = isEditing.value ? 'atualizada' : 'cadastrada'
    emit('show-notification', {
      message: `Organização ${item.value.nomeFantasia} ${acao} com sucesso!`,
      type: 'success'
    })
  } catch (error) {
    console.error('Erro ao salvar Organizacoes:', error)
    let mensagemErro = 'Não foi possível salvar a Organização.'

    if (error.response?.status === 401) {
      mensagemErro = 'Sua sessão expirou ou é inválida. Tente fazer login novamente.'
    } else if (error.response?.status === 400 && error.response.data?.errors) {
      const backendErrors = error.response.data.errors
      const normalizedErrors = {}

      for (const [key, msgs] of Object.entries(backendErrors)) {
        const cleanKey = key.replace(/^(?:\$\.|model\.|item\.)/i, '')
        const camelKey = cleanKey.charAt(0).toLowerCase() + cleanKey.slice(1)
        normalizedErrors[camelKey] = msgs[0]
      }

      // Preserva o Proxy reativo
      Object.assign(errors.value, normalizedErrors)

      const primeiroErro = Object.values(backendErrors).flat()[0]
      const match = primeiroErro?.match(/The (.+) field is required/i)
      if (match) {
        const campoBackend = match[1]
        const labelFrontend = mapaCampos[campoBackend] || campoBackend.replace(/^Lst/, '')
        mensagemErro = `O campo "${labelFrontend}" falhou na validação de servidor.`
      } else {
        mensagemErro = primeiroErro
      }
    } else if (error.response?.data && typeof error.response.data === 'string') {
      mensagemErro = error.response.data
    } else if (error.response?.data?.message || error.response?.data?.title) {
      mensagemErro = error.response.data.message || error.response.data.title
    }

    emit('show-notification', { message: mensagemErro, type: 'error' })
    await focarPrimeiroErro()
  } finally {
    isSaving.value = false
  }
}

function toggleAccordion (key) {
  accordionState[key] = !accordionState[key]
}

function getRegimeTributarioTexto (idRegime) {
  const regimeObj = (listas.LstRegimeTributario || []).find(r => r.idListaClienteItem === idRegime)
  return regimeObj ? regimeObj.textoLista : idRegime
}

function getStatusFilialTexto (idStatus) {
  if (!idStatus) return '-'
  const statusStr = String(idStatus)
  const statusObj = (listas.LstStatusFilial || []).find(s => String(s.idListaClienteItem) === statusStr)
  return statusObj ? statusObj.textoLista : idStatus
}

// --- Estado da Auditoria de Filial ---
const mostrarAuditoriaFilial = ref(false)
const filialParaAuditoria = ref(null)

const abrirAuditoriaFilial = (fil) => {
  filialParaAuditoria.value = fil
  mostrarAuditoriaFilial.value = true
}
</script>

<template>
  <div class="content-area-sub" :class="{ 'panel-is-open': isFilialPanelOpen }">
    <div v-if="isLoadingData" class="d-flex justify-content-center align-items-center h-100 py-5 grid-left">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div v-else class="grid-left" :class="{'list-collapsed': isFilialPanelOpen}">
      <FormularioCadastro
        module-name="Organização"
        :is-loading="isSaving"
        :is-editing="isEditing"
        :entity-info="entityInfo"
        :accordion-state="accordionState"
        @submit="salvarOrganizacao"
        @cancelar="handleCancel"
        @show-notification="(p) => emit('show-notification', p)"
      >
        <template #acoes-header v-if="isEditing">
          <button
            type="button"
            class="btn-icon-minimalista me-3"
            @click="mostrarAuditoria = true"
            title="Ver Histórico de Alterações"
          >
            <i class="bi bi-clock-history"></i>
          </button>
        </template>

        <!-- FRAME 1: DADOS DA ORGANIZAÇÃO -->
        <div class="card mb-3">
          <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="toggleAccordion('basico')">
            <h5 class="mb-0"><i class="bi bi-building me-2"></i>Dados da Organização</h5>
            <i class="bi" :class="accordionState.basico ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>

          <div v-show="accordionState.basico" class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <CampoInput id="razaoSocial" label="Razão Social" v-model="item.razaoSocial" required />
              </div>
              <div class="col-md-6">
                <CampoInput id="nomeFantasia" label="Nome Fantasia" v-model="item.nomeFantasia" required />
              </div>
              <div class="col-md-6">
                <CampoSelect
                  id="lstRegimeTributario"
                  label="Regime Tributário"
                  v-model="item.lstRegimeTributario"
                  :options="listas.LstRegimeTributario || []"
                  option-value="idListaClienteItem"
                  option-label="textoLista"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <!-- FRAME 2: FILIAIS -->
        <div class="card mb-3 border-primary">
          <div class="card-header bg-primary text-white cursor-pointer d-flex justify-content-between align-items-center" @click="toggleAccordion('filiais')">
            <h5 class="mb-0 text-white"><i class="bi bi-diagram-2 me-2"></i>Filiais e Estoques</h5>
            <div class="d-flex align-items-center gap-2">
              <button
                type="button"
                class="btn btn-sm btn-light py-0"
                @click.stop="iniciarNovaFilial"
                :disabled="!item.idOrganizacao"
                :title="!item.idOrganizacao ? 'Salve a organização primeiro para adicionar filiais' : 'Adicionar Filial'"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
              <i class="bi text-white" :class="accordionState.filiais ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
          </div>
          <div v-show="accordionState.filiais" class="card-body border-top border-secondary-subtle p-0">
            <div class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th style="width: 60px;">ID</th>
                    <th style="width: 200px;">Identificação</th>
                    <th style="width: 180px;">CNPJ</th>
                    <th style="width: 80px;">UF</th>
                    <th>Regime Tributário</th>
                    <th style="width: 120px;">Status</th>
                    <th style="width: 100px;">Qtd Locais</th>
                    <th class="text-end" style="width: 60px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(fil, idx) in item.filiais" :key="idx">
                    <td class="ps-3">{{ fil.idFilial }}</td>
                    <td class="fw-bold">{{ fil.identificacao || fil.enderecoDescricao || '---' }}</td>
                    <td>{{ fil.cnpj }}</td>
                    <td>{{ fil.uf }}</td>
                    <td>{{ getRegimeTributarioTexto(item.lstRegimeTributario) }}</td>
                    <td>{{ getStatusFilialTexto(fil.lstStatusFilial) }}</td>
                    <td>
                      <span class="badge bg-secondary">{{ fil.locaisEstoque?.length || 0 }} locais</span>
                    </td>
                    <td class="acoes-cell text-end pe-3">
                      <button type="button" class="btn-icon" title="Editar" @click="editarFilial(fil, idx)"><i class="bi bi-pencil-fill"></i></button>
                      <button type="button" class="btn-icon text-secondary" title="Ver Histórico" @click="abrirAuditoriaFilial(fil)"><i class="bi bi-clock-history"></i></button>
                    </td>
                  </tr>
                  <tr v-if="!item.filiais || item.filiais.length === 0"><td colspan="8" class="text-center py-4 text-muted">Nenhuma filial registada.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </FormularioCadastro>
    </div>

    <div v-if="isFilialPanelOpen" class="divider-sub" @click="toggleFilialPanel()">
      <span class="divider-text-sub">Dados da Organização</span>
      <i :class="['divider-icon-sub bi', isFilialPanelOpen ? 'bi-arrow-right-short' : 'bi-arrow-left-short']"></i>
    </div>

    <div class="admin-panel-sub">
      <div class="admin-panel-body-sub">
        <FilialCadastro
          v-if="isFilialPanelOpen"
          :item-para-editar="filialSelecionada"
          :listas="listas"
          :id-organizacao="item.idOrganizacao"
          @item-atualizado="handleFilialAtualizada"
          @cancelar="isFilialPanelOpen = false"
          @show-notification="(p) => emit('show-notification', p)"
        />
      </div>
    </div>

    <AuditLogViewer
      v-if="mostrarAuditoria"
      tabela="Organizacao"
      :chave-primaria="item.idOrganizacao"
      :titulo-registro="item.nomeFantasia || item.razaoSocial || 'Organização'"
      :tabelas-filhas="['Filial']"
      chave-estrangeira="IdOrganizacao"
      :dicionarios="{}"
      @fechar="mostrarAuditoria = false"
    />

    <AuditLogViewer
      v-if="mostrarAuditoriaFilial && filialParaAuditoria"
      tabela="Filial"
      :chave-primaria="filialParaAuditoria.idFilial"
      :titulo-registro="filialParaAuditoria.enderecoDescricao || filialParaAuditoria.cnpj || 'Filial'"
      :tabelas-filhas="['LocalEstoque']"
      chave-estrangeira="IdFilial"
      :dicionarios="{
        'lstStatusFilial': listas.LstStatusFilial || []
      }"
      @fechar="mostrarAuditoriaFilial = false"
    />
  </div>
</template>

<style scoped>
.content-area-sub {
  display: grid;
  grid-template-columns: 1fr 0px 0fr;
  transition: grid-template-columns 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);
  height: calc(100% + 3rem);
  width: calc(100% + 3rem);
  margin: -1.5rem;
  position: relative;
}
.content-area-sub.panel-is-open {
  grid-template-columns: 0fr 21px 1fr;
}

.grid-left {
  grid-column: 1;
  overflow-y: auto;
  transition: padding 0.3s;
  padding: 1.5rem;
}
.grid-left.list-collapsed {
  padding: 0;
}

.divider-sub {
  grid-column: 2;
  width: 21px;
  cursor: pointer;
  background-color: var(--bs-tertiary-bg);
  border-left: 1px solid var(--bs-border-color-translucent);
  border-right: 1px solid var(--bs-border-color-translucent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 1025;
  color: var(--bs-body-color);
  transition: background-color 0.2s ease, color 0.2s ease;
}
.divider-sub:hover {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-emphasis);
}
.divider-text-sub {
  transform: rotate(-180deg);
  white-space: nowrap;
  writing-mode: vertical-lr;
  font-size: 1rem;
  margin-bottom: 5px;
}
.divider-icon-sub {
  font-size: 2rem;
}

.admin-panel-sub {
  grid-column: 3;
  height: 100%;
  box-shadow: -5px 0 20px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  z-index: 1020;
  overflow: hidden;
}
.admin-panel-body-sub {
  padding: 0;
  overflow-y: auto;
  flex-grow: 1;
  background-color: var(--bs-body-bg);
}

.btn-icon-minimalista { background: none; border: none; color: var(--bs-body-color); cursor: pointer; }
.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.25rem;
  overflow-x: auto;
}
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: var(--bs-body-emphasis); }
.btn-xs { padding: 0.1rem 0.4rem; font-size: 0.75rem; }
</style>
